import { inject, Injectable } from '@angular/core';
import { map, Observable, withLatestFrom } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { selectCounties } from '../state/location/location.reducer';
import { Territory } from '../core/classes/territory';
import { GenEventCount } from '../generated/serverModels/GenEventCount';
import { MetricDetails } from '../shared/classes/metric-details';
import { GenStateRestorationMetric } from '../generated/serverModels/GenStateRestorationMetric';
import { IceService } from './ice.service';

export interface GeoJsonGeometry {
  type: string;
  coordinates: unknown;
}

export interface MetricLayerFeature {
  type: string;
  geometry: GeoJsonGeometry;
  properties: {
    county: string;
    stateAbbreviation: string;
    tastiDetails?: MetricDetails;
    ntlDetails?: MetricDetails;
  };
}

export interface MetricLayerResponse {
  features: MetricLayerFeature[];
  scale: {
    min: number;
    max: number;
    toValue: (value: number) => number;
  };
}
@Injectable({
  providedIn: 'root',
})
export class RestorationService {

  private readonly baseUrl = 'api/metrics';
  private http = inject(HttpClient);
  private store = inject(Store);

  constructor() {
  }

  public getStateLevelMetrics(states: Territory[], years: number[]): Observable<GenStateRestorationMetric[]> {
    const url = `${this.baseUrl}/state-level-metrics`;

    let params = new HttpParams();
    if (states) {
      params = params.set('states', states.map(s => s.abbreviation).join());
    }
    if (years) {
      params = params.set('years', years.join());
    }

    return this.http.get<GenStateRestorationMetric[]>(url, { params }).pipe(
      map(response => response.map(metric => new GenStateRestorationMetric(metric))),
    );
  }


  public getMetricLayer(
    desiredMetric: string = 'auc',
    aggregate: 'min' | 'max' | 'avg' | 'sum',
    territories: Territory[],
    years: number[],
    includeNtlData: boolean = true,
  ): Observable<MetricLayerResponse> {
    const url = `${this.baseUrl}/by-type-by-year`;

    let params = new HttpParams()
      .set('type', desiredMetric)
      .set('years', years.join());

    params = params.append('states', territories.map(t => t.abbreviation).join());

    return this.http.get<MetricDetails[]>(url, { params }).pipe(
      map(metrics => metrics.map(metric => new MetricDetails(metric))),
      withLatestFrom(this.store.select(selectCounties)),
      map(value => {
        const metrics = value[0];

        const territoryIds = territories.map(t => t.id);
        const counties = value[1].filter(c => territoryIds.includes(c.stateId));


        const features = counties.map(county => {
          const countyMetrics = metrics.filter(m => m.fipsCode === county.geoid);
          let tastiMetric = countyMetrics.find(m => m.source?.name === 'TASTIGRID');
          const ntlMetric = includeNtlData ? countyMetrics.find(m => m.source?.name === 'NTL') : undefined;
          if (countyMetrics.length === 0) {
            tastiMetric = new MetricDetails();
            tastiMetric.county = county.name;
            tastiMetric.stateAbbreviation = county.stateAbbreviation;
            tastiMetric.fipsCode = county.geoid;
          }
          return {
            type: 'Feature',
            geometry: JSON.parse(county.boundary),
            properties: {
              tastiDetails: tastiMetric,
              ntlDetails: ntlMetric,
              county: county.name,
              stateAbbreviation: county.stateAbbreviation,
            },
          };
        });



        const ret = {
          features: features,
          scale: IceService.getNormalizedScale(features.map(feature => feature?.properties.tastiDetails?.[aggregate] as number).filter(value => value !== undefined) as number[]),
        };
        return ret;

      }),
    );
  }

  getOutageEventCount(years: number[], states: Territory[], restorationPercent?: string, minRestorationTime?: number): Observable<{ type: string; features: unknown[] }> {
    const url = `${this.baseUrl}/eventCountByYear`;

    let params = new HttpParams();

    params = params.append('years', years.join());

    if (restorationPercent !== undefined) {
      params = params.append('res', restorationPercent);
    }

    if (minRestorationTime !== undefined) {
      params = params.append('hours', minRestorationTime);
    }

    if (states && states?.length !== 0) {
      params = params.append('states', states.map((state) => state.abbreviation).join());
    }

    return this.http.get<GenEventCount[]>(url, { params }).pipe(
      map(eventCounts => eventCounts.map(eventCount => new GenEventCount(eventCount))),
      withLatestFrom(this.store.select(selectCounties)),
      map(data => {
        const eventMap = data[0].reduce((prev, current) => prev.set(current.fipsCode, current), new Map<string, GenEventCount>());
        const territoryIds = states.map(t => t.id);
        const counties = data[1].filter(c => territoryIds.includes(c.stateId));


        const features = counties.map(county => {
          let eventCount = eventMap.get(county.geoid);
          if (eventCount === undefined) {
            eventCount = new GenEventCount();
            eventCount.name = county.name;
            eventCount.abbreviation = county.stateAbbreviation;
            eventCount.fipsCode = county.geoid;
          }
          return {
            type: 'Feature',
            properties: eventCount,
            geometry: JSON.parse(county.boundary),
          };
        });

        return {
          type: 'FeatureCollection',
          features,
        };
      }),
    );

  }
}
